/**
 * PublicRecording page - Modernized UI with smooth state transitions
 *
 * This component provides a public interface for AI Scribe recording
 * that can be embedded in an iframe from external applications.
 *
 * Supports mock mode via ?mock=true URL parameter for UI testing
 * without API calls or actual recording.
 */

import { envConfig } from '@/lib/env-config';
import { PublicRecordingService } from '@/lib/public-recording-service';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Types
import type { PublicRecordingParams } from '@/types/public-recording';

// Hooks
import { useMockRecording } from '@/hooks/use-mock-recording';
import { usePublicRecordingHandlers } from '@/hooks/use-public-recording-handlers';
import { usePublicRecordingInit } from '@/hooks/use-public-recording-init';
import { usePublicRecordingWaveform } from '@/hooks/use-public-recording-waveform';

// Components
import { CompactErrorState } from '@/components/PublicRecording/CompactErrorState';
import { CompactLoadingState } from '@/components/PublicRecording/CompactLoadingState';
import { CompactRecordingControls } from '@/components/PublicRecording/CompactRecordingControls';
import { CompactUploadingState } from '@/components/PublicRecording/CompactUploadingState';

// ============================================================================
// Shared render logic
// ============================================================================
interface RecordingState {
  isValidating: boolean;
  isUploading: boolean;
  isProcessing: boolean;
  isValidated: boolean;
  scribeJobId: string | null;
  error: string | null;
  isRecording: boolean;
  isPaused: boolean;
  audioLevel: number;
  handleStartRecording: () => void;
  handlePauseRecording: () => void;
  handleResumeRecording: () => void;
  handleStopRecording: () => void;
}

const RecordingUI = ({
  isValidating,
  isUploading,
  isProcessing,
  isValidated,
  scribeJobId,
  error,
  isRecording,
  isPaused,
  audioLevel,
  handleStartRecording,
  handlePauseRecording,
  handleResumeRecording,
  handleStopRecording,
}: RecordingState) => {
  const getStateKey = () => {
    if (isValidating) return 'loading';
    if (isUploading) return 'uploading';
    if (isProcessing) return 'processing';
    if (error || !isValidated || !scribeJobId) return 'error';
    return 'recording';
  };

  const stateKey = getStateKey();

  return (
    <div className="flex items-center justify-center h-full">
      <AnimatePresence mode="wait">
        {stateKey === 'loading' && <CompactLoadingState key="loading" />}

        {stateKey === 'uploading' && (
          <CompactUploadingState key="uploading" phase="uploading" />
        )}

        {stateKey === 'processing' && (
          <CompactUploadingState key="processing" phase="processing" />
        )}

        {stateKey === 'error' && (
          <CompactErrorState key="error" error={error} />
        )}

        {stateKey === 'recording' && (
          <motion.div
            key="recording"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="w-full">
            <CompactRecordingControls
              isRecording={isRecording}
              isPaused={isPaused}
              audioLevel={audioLevel}
              onStartRecording={handleStartRecording}
              onPauseRecording={handlePauseRecording}
              onResumeRecording={handleResumeRecording}
              onStopRecording={handleStopRecording}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================================
// Mock mode component — no API calls, simulated audio
// ============================================================================
const PublicAiScribeMock = () => {
  const {
    isValidated,
    isValidating,
    scribeJobId,
    error,
    isRecording,
    isPaused,
    isUploading,
    isProcessing,
    audioLevel,
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
  } = useMockRecording();

  return (
    <RecordingUI
      isValidating={isValidating}
      isUploading={isUploading}
      isProcessing={isProcessing}
      isValidated={isValidated}
      scribeJobId={scribeJobId}
      error={error}
      isRecording={isRecording}
      isPaused={isPaused}
      audioLevel={audioLevel}
      handleStartRecording={handleStartRecording}
      handlePauseRecording={handlePauseRecording}
      handleResumeRecording={handleResumeRecording}
      handleStopRecording={handleStopRecording}
    />
  );
};

// ============================================================================
// Real mode component — full API integration
// ============================================================================
const PublicAiScribeReal = ({ params }: { params: PublicRecordingParams }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recordingServiceRef = useRef<PublicRecordingService | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const latestDxListRef = useRef<any>(null);

  const {
    isValidated,
    isValidating,
    scribeJobId,
    gatewayToken,
    error: initError,
  } = usePublicRecordingInit(params);

  const { audioLevel } = usePublicRecordingWaveform(
    recordingServiceRef,
    isRecording,
    isPaused
  );

  const {
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
  } = usePublicRecordingHandlers({
    recordingServiceRef,
    scribeJobId,
    appointmentId: params.appointmentId,
    patientId: params.patientId,
    locationId: params.locationId,
    sectionTag: params.sectionTag,
    clientPlatform: params.clientPlatform,
    patientCode: params.patientCode,
    gatewayToken,
    setIsRecording,
    setIsPaused,
    setIsUploading,
    setIsProcessing,
    setError,
    latestDxListRef,
  });

  // Initialize recording service. Now requires the Deepgram API key —
  // the service streams audio to Nova-3 in parallel with PCM capture so
  // the gateway call can include the transcription directly.
  useEffect(() => {
    if (recordingServiceRef.current) return;
    const apiKey = envConfig.deepgram.apiKey;
    if (!apiKey) {
      console.error(
        '[PublicAiScribe] VITE_DEEPGRAM_API_KEY is not configured'
      );
      setError(
        'Recording service is not configured. Missing Deepgram API key.'
      );
      return;
    }
    try {
      recordingServiceRef.current = new PublicRecordingService(apiKey);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to init service');
    }
  }, []);

  // Message listener to capture messages from parent window
  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      console.log('[PublicAiScribe] Message received:', event.data);
      if (event.data.type === 'dxList') {
        latestDxListRef.current = event.data.payload;
      }
    };

    window.addEventListener('message', messageHandler);
    return () => window.removeEventListener('message', messageHandler);
  }, []);

  // Send greeting to parent window when ready
  useEffect(() => {
    if (isValidated && scribeJobId && !isValidating && !initError && !error) {
      console.log(
        '[PublicAiScribe] Page is ready, sending greeting to parent window...'
      );

      if (window.parent) {
        window.parent.postMessage(
          {
            type: 'greeting',
            payload: {
              message: 'EyeVantage Public Recording is ready',
              scribeJobId,
              sectionTag: params.sectionTag,
              timestamp: new Date().toISOString(),
            },
          },
          '*'
        );
        handleStartRecording();
        console.log('[PublicAiScribe] Greeting sent to parent window');
      }
    }
  }, [
    isValidated,
    scribeJobId,
    isValidating,
    initError,
    error,
    params.sectionTag,
    handleStartRecording,
  ]);

  // Sync error from initialization
  useEffect(() => {
    if (initError) {
      setError(initError);
    }
  }, [initError]);

  return (
    <RecordingUI
      isValidating={isValidating}
      isUploading={isUploading}
      isProcessing={isProcessing}
      isValidated={isValidated}
      scribeJobId={scribeJobId}
      error={error}
      isRecording={isRecording}
      isPaused={isPaused}
      audioLevel={audioLevel}
      handleStartRecording={handleStartRecording}
      handlePauseRecording={handlePauseRecording}
      handleResumeRecording={handleResumeRecording}
      handleStopRecording={handleStopRecording}
    />
  );
};

// ============================================================================
// Main entry point — routes to mock or real based on URL param
// ============================================================================
const PublicAiScribe = () => {
  const [searchParams] = useSearchParams();
  const isMockMode = searchParams.get('mock') === 'true';

  const params: PublicRecordingParams = {
    appointmentId: searchParams.get('appointmentId') || '',
    patientId: searchParams.get('patientId') || '',
    locationId: searchParams.get('locationId') || '',
    sectionTag: searchParams.get('sectionTag') || '',
    integrationKey: searchParams.get('integrationKey') || '',
    partnerName: searchParams.get('partnerName') || '',
    debugMode: searchParams.get('debug') === 'true',
    patientFirstName: searchParams.get('patientFirstName') || undefined,
    patientLastName: searchParams.get('patientLastName') || undefined,
    patientDob: searchParams.get('patientDob') || undefined,
    clientPlatform: searchParams.get('clientPlatform') || undefined,
    patientCode: searchParams.get('patientCode') || '',
  };

  // Make background transparent for iframe embedding
  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    return () => {
      document.body.style.backgroundColor = prevBg;
      document.documentElement.style.backgroundColor = prevBg;
    };
  }, []);

  if (isMockMode) {
    return <PublicAiScribeMock />;
  }

  return <PublicAiScribeReal params={params} />;
};

export default PublicAiScribe;
